import { Injectable } from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'

// Lightweight sanitizer: rely on DOMPurify if available, otherwise a tiny allowlist scrubber
declare const DOMPurify: any

export interface FormatOptions {
  demoteHeadings?: boolean
  linkify?: boolean
}

@Injectable({ providedIn: 'root' })
export class JobDescriptionFormatterService {
  private cache = new Map<string, SafeHtml>()

  public constructor(private readonly sanitizer: DomSanitizer) {}

  public format(rawHtml: string, options: FormatOptions = {}): SafeHtml {
    const key = this.cacheKey(rawHtml, options)
    const cached = this.cache.get(key)
    if (cached) { return cached }

    const normalized = this.normalize(rawHtml)
    const sanitized = this.sanitize(normalized)
    const transformed = this.transform(sanitized, options)
    const html = this.sanitizer.bypassSecurityTrustHtml(transformed)
    this.cache.set(key, html)
    return html
  }

  private cacheKey(raw: string, options: FormatOptions): string {
    return JSON.stringify({ r: raw?.slice(0, 10000), o: options })
  }

  private normalize(html: string): string {
    if (!html) return ''
    // Replace common entities and collapse &nbsp; (except for our spacer marker)
    let s = html
      .replace(/&nbsp;/gi, ' ')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+$/gm, '')
      .replace(/&ndash;/gi, '–')
      .replace(/&mdash;/gi, '—')
      .replace(/&euro;/gi, '€')
      .replace(/&ouml;/gi, 'ö')
      .replace(/&auml;/gi, 'ä')
      .replace(/&uuml;/gi, 'ü')
      .replace(/&Ouml;/gi, 'Ö')
      .replace(/&Auml;/gi, 'Ä')
      .replace(/&Uuml;/gi, 'Ü')
      .replace(/&eacute;/gi, 'é')
      .replace(/&egrave;/gi, 'è')
      .replace(/&rsquo;/gi, '\'')
      .replace(/&lsquo;/gi, '\'')
      .replace(/&rdquo;/gi, '"')
      .replace(/&ldquo;/gi, '"')
    // Convert sequences of <br> into paragraph breaks
    // 3+ <br> in a row → paragraph break + explicit spacer paragraph + new paragraph
    // 2 <br> in a row → normal paragraph break
    s = s
      .replace(/(<br\s*\/?\s*>\s*){3,}/gi, '</p><p class="jd-spacer"></p><p>')
      .replace(/(<br\s*\/?\s*>\s*){2}/gi, '</p><p>')
    // If there are list-looking lines that are not wrapped, convert to UL
    // e.g., <li style="...">…</li> or lines starting with •/- → wrap into proper <ul><li>
    s = s.replace(/<li[^>]*>/gi, '<li>')
    // Ensure it's wrapped in a container so DOM parsing is easier
    return `<div>${s}</div>`
  }

  private sanitize(html: string): string {
    const allowedTags = ['h1','h2','h3','h4','p','ul','ol','li','a','strong','b','em','i','code','blockquote','hr','br','div','span']
    const allowedAttrs: Record<string, string[]> = {
      'a': ['href','title','target','rel'],
      'p': ['class'],
      'span': [],
      'div': [],
      'code': []
    }
    if (typeof DOMPurify !== 'undefined' && DOMPurify?.sanitize) {
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: allowedTags,
        ALLOWED_ATTR: Array.from(new Set(Object.values(allowedAttrs).flat())),
        // strip everything else
        FORBID_ATTR: ['style','onerror','onclick','id'],
        FORBID_TAGS: [],
        RETURN_TRUSTED_TYPE: false,
      })
    }
    // Fallback: basic stripping using DOM
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT)
    const toRemove: Element[] = []
    while (walker.nextNode()) {
      const el = walker.currentNode as Element
      if (!allowedTags.includes(el.tagName.toLowerCase())) { toRemove.push(el); continue }
      // remove all attributes except allowed
      const keep = new Set(allowedAttrs[el.tagName.toLowerCase()] || [])
      Array.from(el.attributes).forEach(attr => {
        if (!keep.has(attr.name)) { el.removeAttribute(attr.name) }
      })
    }
    toRemove.forEach(el => el.replaceWith(...Array.from(el.childNodes)))
    return doc.body.innerHTML
  }

  private transform(sanitized: string, options: FormatOptions): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(sanitized, 'text/html')
    const root = doc.body

    // Demote headings to keep page hierarchy
    if (options.demoteHeadings !== false) {
      this.demoteHeadingLevel(root, 'h1', 'h2')
      this.demoteHeadingLevel(root, 'h2', 'h3')
    }

    // Identify and mark the job title (first h3 with strong) and intro section
    let titleElement: Element | null = null
    let foundTitle = false
    let foundSectionHeading = false
    
    const children = Array.from(root.children)
    
    // Find the title (first h3 > strong)
    for (const child of children) {
      if (child.tagName.toLowerCase() === 'h3') {
        const hasStrong = child.querySelector('strong')
        if (hasStrong && !foundTitle) {
          titleElement = child
          titleElement.classList.add('jd-title')
          foundTitle = true
          break
        }
      }
    }
    
    // Collect intro paragraphs (everything between title and next h3)
    const introElements: Element[] = []
    let collectingIntro = false
    
    for (const child of children) {
      if (child === titleElement) {
        collectingIntro = true
        continue
      }
      
      if (collectingIntro) {
        if (child.tagName.toLowerCase() === 'h3') {
          foundSectionHeading = true
          break
        }
        if (child.tagName.toLowerCase() === 'p') {
          introElements.push(child)
        }
      }
    }
    
    // If we have intro paragraphs, wrap them
    if (introElements.length > 0) {
      const introDiv = doc.createElement('div')
      introDiv.className = 'jd-intro'
      
      introElements.forEach(el => {
        introDiv.appendChild(el.cloneNode(true))
        if (el.parentNode) {
          el.parentNode.removeChild(el)
        }
      })
      
      // Insert intro div after title
      if (titleElement && titleElement.nextSibling) {
        root.insertBefore(introDiv, titleElement.nextSibling)
      } else if (titleElement) {
        root.appendChild(introDiv)
      }
    }
    
    // Wrap any remaining loose text nodes
    Array.from(root.childNodes).forEach(node => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        const p = doc.createElement('p')
        p.textContent = node.textContent
        if (node.parentNode) {
          node.parentNode.replaceChild(p, node)
        }
      }
    })

    // Strip inline styles/classes, normalize lists (but preserve our semantic classes)
    root.querySelectorAll('*').forEach(el => {
      el.removeAttribute('style')
      el.removeAttribute('id')
      // Only remove class if it's not one of our semantic classes
      const currentClass = el.getAttribute('class')
      if (currentClass && !['jd-intro', 'jd-title', 'jd-spacer'].includes(currentClass)) {
        el.removeAttribute('class')
      }
      if (el.tagName.toLowerCase() === 'a') {
        const href = (el.getAttribute('href') || '').trim()
        if (!/^https?:|^mailto:|^tel:/i.test(href)) {
          el.removeAttribute('href')
        } else {
          el.setAttribute('rel','noopener noreferrer')
          if (!/^mailto:|^tel:/i.test(href)) el.setAttribute('target','_blank')
        }
      }
    })

    // If vendor lists were paragraphs with dashes, convert to UL
    const paragraphs = Array.from(root.querySelectorAll('p'))
    paragraphs.forEach(p => {
      const text = (p.textContent || '').trim()
      if (/^[-•\u2022]/.test(text)) {
        // Aggregate consecutive dash bullets into one UL
        const ul = doc.createElement('ul')
        let cursor: Element | null = p
        while (cursor) {
          const t = (cursor.textContent || '').trim()
          if (!/^[-•\u2022]/.test(t)) break
          const li = doc.createElement('li')
          li.textContent = t.replace(/^[-•\u2022]\s*/, '')
          ul.appendChild(li)
          const next = cursor.nextElementSibling as Element | null
          cursor.remove()
          cursor = (next && next.tagName.toLowerCase() === 'p') ? next : null
        }
        p.parentElement?.insertBefore(ul, p)
      }
    })

    // Just unwrap em/i tags - keep text plain, but DO NOT destroy surrounding spaces.
    // We avoid trimming the inner text because some job descriptions rely on spaces
    // living inside <em>/<i> for word separation (e.g. 'Als<em> Security Engineer</em>ben').
    root.querySelectorAll('em,i').forEach(el => {
      const text = el.textContent || ''
      // If element is effectively empty, drop it
      if (!text.trim()) {
        el.replaceWith('')
        return
      }
      const textNode = doc.createTextNode(text)
      el.replaceWith(textNode)
    })

    // Remove empty paragraphs/list-items, but keep semantic spacer paragraphs
    root.querySelectorAll('p,li').forEach(el => {
      if (el.classList.contains('jd-spacer')) { return }
      if (!(el.textContent || '').trim()) el.remove()
    })

    return root.innerHTML
  }

  private demoteHeadingLevel(root: HTMLElement, from: string, to: string) {
    root.querySelectorAll(from).forEach(el => {
      const replacement = el.ownerDocument!.createElement(to)
      replacement.innerHTML = el.innerHTML
      el.replaceWith(replacement)
    })
  }
}


